import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { PersonCard } from '@/api/people';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { Score } from '@/components/ui/Score';
import { StatusDot } from '@/components/ui/StatusDot';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

interface Props {
  person: PersonCard;
}

/**
 * `DESIGN.md` §6.5 — *"Summary row. Three figures under an eyebrow, hairline-divided: average across
 * submitted interviews, submitted vs total, latest recommendation. All computed on read — never
 * stored."*
 *
 * ⚠️ **Every figure here is viewer-dependent, and this component computes none of them**
 * (feedback protection #3, ADR 0006 + backend TASK-033). `average_score` and `latest_recommendation`
 * are computed by the API over the caller's **own blind-filtered** set, so a viewer holding any
 * unsubmitted interview for this person is sent `null` where a colleague who owes nothing is sent the
 * figure — for an Admin and a Recruiter too, because the gate keys on owing feedback and not on
 * group. Two viewers legitimately receive different bodies from the same URL, which is why the cache
 * key carries the viewer (`queryKeys.person`).
 *
 * **The asymmetry between `null` and `0` is the design, not a gap** (`PersonCardOut`'s docstring, and
 * `AverageScoreOut`'s): the counters are `0` because a count of nothing is genuinely zero, and the
 * two feedback values are `null` because an average of nothing is *not* a zero average — a rendered
 * `0` would enter the reader's arithmetic as "this candidate scored zero". `Score` renders `null` as
 * an em dash for exactly that reason and the counters are printed as they arrive.
 */
export function SummaryRow({ person }: Props) {
  const { t } = useTranslation('candidates');

  return (
    <section className="mt-8" aria-label={t('card.summary.label')}>
      <Eyebrow>{t('card.summary.label')}</Eyebrow>

      {/*
       * §6.5's *"hairline-divided"*, and §10's *"below `lg` … one column"*: the rules run between
       * the columns above `lg` and between the rows below it, so the row never becomes three
       * squeezed columns on a laptop turned sideways. `divide-*` is the hairline — `border-rule` —
       * and not a heavier border.
       */}
      <dl className="flex flex-col divide-y divide-rule border-y border-rule lg:flex-row lg:divide-x lg:divide-y-0">
        <Figure label={t('card.summary.average')}>
          <Score value={person.average_score} />
          <NoAverageNote person={person} />
        </Figure>

        {/*
         * §6.3's `3 / 4`, at the card's scope: *submitted over total*, straight from the payload.
         *
         * ⚠️ **The denominator counts drafts, on purpose** (ADR 0007's *"What the draft still counts
         * towards"*, `backend/CONTEXT.md`'s derived-values table, and
         * `.claude/rules/feedback-integrity.md`'s stated exception). `interview_count` is a
         * **visibility** number: an outstanding conversation is visible without opening anything,
         * which is the leak the product exists to show. A draft still enters no average, no latest
         * recommendation and no status move — those are this same payload's other fields, and they
         * are submitted-only.
         */}
        <Figure label={t('card.summary.interviews')}>
          <span className={cn(type.numeral, 'text-sm text-ink-900')}>
            {t('card.summary.count', {
              submitted: person.submitted_interview_count,
              total: person.interview_count,
            })}
          </span>
        </Figure>

        <Figure label={t('card.summary.latest')}>
          {/* The document types this `OverallRecommendation | null` since backend TASK-104, so a
              verdict outside the four is not on the wire; there is no client narrowing behind that
              any more (BUG-038), and `StatusDot` renders nothing rather than throwing if one ever
              arrives. */}
          {person.latest_recommendation ? (
            <StatusDot value={person.latest_recommendation} />
          ) : (
            <span className="text-sm text-ink-500" role="img" aria-label={t('latest.none')}>
              —
            </span>
          )}
        </Figure>
      </dl>
    </section>
  );
}

/** One figure: its label as the term, the figure as the description (§9 — a real label, not a div). */
function Figure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex-1 px-0 py-4 lg:px-6 lg:first:pl-0 lg:last:pr-0">
      <dt className={cn(type.metadata, 'mb-2')}>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * ⚠️ **Why an em dash needs a sentence beside it, on this screen and not on §6.3's row.**
 *
 * `null` means three different things to a human and the same thing to the database — *no interviews
 * at all*, *only drafts so far*, and *submitted feedback exists and is not yours to read yet* (ADR
 * 0006) — and `AverageScoreOut`'s own docstring says the payload carries no sentinel for them
 * because *"the payload already carries `interview_count` and `submitted_interview_count`, which
 * tell them apart"*. This is where that is read: the card is the screen where a reader stops and
 * asks *why* there is no average, and an unexplained dash on the most-read screen in the product is
 * how somebody concludes the feedback was lost.
 *
 * **Nothing here is derived and nothing is a second request.** The branch is on the two counters the
 * API already sent, in the order the backend's own docstring lists the cases; it never asserts an
 * average, and it never claims to know a value the API withheld.
 *
 * The last case is deliberately **silent**: `average_score` is `null` while `latest_recommendation`
 * is not, which can only happen when feedback *is* open to this viewer and no question was scored in
 * it. The verdict is on screen a hand away, so a sentence about hidden feedback there would be
 * false — and inventing one about unscored questions would be a claim this payload cannot support.
 */
function NoAverageNote({ person }: { person: PersonCard }) {
  const { t } = useTranslation('candidates');

  if (person.average_score !== null) return null;

  if (person.interview_count === 0) return <Note>{t('card.summary.noInterviews')}</Note>;
  if (person.submitted_interview_count === 0) return <Note>{t('card.summary.noneSubmitted')}</Note>;
  if (person.latest_recommendation === null) return <Note>{t('card.summary.blinded')}</Note>;

  return null;
}

function Note({ children }: { children: ReactNode }) {
  return <p className={cn(type.metadata, 'mt-2 max-w-[28ch]')}>{children}</p>;
}
